import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import {
  Circuit,
  Day,
  DayExercise,
  GetProgramResopnse,
} from "../../lib/types/programEdit";
import { Box, Button, Collapse, Flex, FormControl, FormLabel, Select, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { CustomTitle } from "./CustomTitle";
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  DeleteIcon,
  DragHandleIcon,
} from "@chakra-ui/icons";
import { DayDetail } from "./DayDetail";
import { ExercisePlan } from "./ExercisePlan";
import { RestDayPlan } from "./RestDayPlan";
import { CircuitPlan } from "./CircuitPlan";
import urlConstants from "../../lib/constants/url.constants";
import useApi from "../../lib/hooks/useApi";
import { ApiCommand } from "../../lib/Api";
import useToastNotification from "../../lib/hooks/useToastNotification";

type DayPlanProps = {
  weekIndex: number;
  dayIndex: number;
  day: Day;
  addDay: (weekIndex: number, newTypeId: number, newFormats: string[]) => void;
  reassignDayTypeIds: (deletedDayTypeId: number) => void;
  program: GetProgramResopnse; // ideally define this type better if known
  updateProgram: (updatedProgram: GetProgramResopnse) => void;
  isWeekCollapsed: boolean;
  updateDays?: (val: any) => void;
  days?: Day[];
  addedNum: number;
  totaldays: number;
  onDayThumbnailFile?: (weekIndex: number,   
  dayIndex: number, file: File | null) =>  
  void; 
};

export const DayPlan = ({
  weekIndex,
  dayIndex,
  day,
  addDay,
  reassignDayTypeIds,
  program,
  updateProgram,
  isWeekCollapsed,
  updateDays = (val) => {},
  days = [],
  addedNum,
  totaldays,
  onDayThumbnailFile,
}: DayPlanProps) => {
  const REST_DAY_FLAG = "rest-day";
  const [isCollapsed, setIsCollapsed] = useState(
    addedNum === day.typeId ? false : true,
  );
  const [addedExNum, setAddedExNum] = useState(0);
  const isRestDay = day.formats.includes(REST_DAY_FLAG);

  const [warmupTemplates, setWarmupTemplates] = useState<{ _id: string; title: string }[]>([]);
  const warmupDetailCache = useRef<Map<string, any>>(new Map());
  const showToast = useToastNotification();
  const { sendRequest: getWarmupTitles } = useApi<{ _id: string; title: string }[]>();
  const { sendRequest: getWarmupDetail } = useApi<any>();

  const fetchWarmupTemplates = () => {
    getWarmupTitles({
      callback: (data, error) => {
        if (error) {
          showToast({
            title: 'Failed to fetch warmups',
            description: error,
            status: 'error',
          });
          return;
        }
        if (data) {
          setWarmupTemplates(data);
        }
      },
      command: ApiCommand.GET,
      url: urlConstants.warmups.getWarmupsTitle,
    });
  };

  useEffect(() => {
    fetchWarmupTemplates();
  }, []);

  const applyWarmupData = (data: any, templateId: string, templateTitle: string, templateInstructions = '') => {
    const updatedProgram = _.cloneDeep(program);
    if (!updatedProgram.weeks[weekIndex].days[dayIndex].warmups) {
      updatedProgram.weeks[weekIndex].days[dayIndex].warmups = [];
    }

    const warmups = updatedProgram.weeks[weekIndex].days[dayIndex].warmups!;

    // Count distinct warmup groups already present (each templateId = one logical warmup)
    const existingGroupCount = new Set(warmups.map((w) => w.warmupTemplateId).filter(Boolean)).size;
    // Use the template's actual name as the group label so the mobile app shows it
    const warmupLabel = templateTitle || `Warmup ${existingGroupCount + 1}`;

    const getNextTypeId = () => {
      const typeIds = warmups.map((w) => w.typeId).filter((id): id is number => typeof id === 'number');
      return Math.max(0, ...typeIds) + 1;
    };

    const getOrder = () =>
      updatedProgram.weeks[weekIndex].days[dayIndex].exercises.length +
      (updatedProgram.weeks[weekIndex].days[dayIndex].circuits || []).length +
      warmups.length + 1;

    const mapExtra = (extra: any) => ({
      sets: extra.sets || 0, reps: extra.reps || 0, weight: extra.weight || 0,
      rest: extra.rest || 0, load: extra.load || 0, per_side: !!extra.per_side,
      custom_text: extra.custom_text || '', type: extra.type || 3,
    });

    const mapExercise = (ex: any, idx: number): DayExercise => ({
      typeId: idx + 1,
      exerciseId: ex.exerciseId?._id || ex.exerciseId?.id || ex.exerciseId || '',
      guide: ex.guide || '', sets: ex.sets || 0, reps: ex.reps || 0,
      weight: ex.weight || 0, rest: ex.rest || 0, per_side: !!ex.per_side,
      custom_text: ex.custom_text || '', formats: ex.formats || [],
      status: '', extra: (ex.extra || []).map(mapExtra),
    });

    // One entry per top-level exercise — marked isExercise so the app renders inline
    (data.exercises || []).forEach((ex: any) => {
      warmups.push({
        rounds: 1, title: warmupLabel, typeId: getNextTypeId(),
        formats: [], circuitExercises: [mapExercise(ex, 0)],
        order: getOrder(), warmupTemplateId: templateId,
        warmupTemplateTitle: warmupLabel,
        warmupInstructions: templateInstructions, isExercise: true,
      } as any);
    });

    // One entry per circuit — use the circuit's own title so the app can show it
    // as the sub-circuit name; the warmup GROUP label comes from the exercise items.
    (data.circuits || []).forEach((circuit: any) => {
      warmups.push({
        rounds: circuit.rounds || 1,
        title: circuit.title || '',
        typeId: getNextTypeId(),
        formats: circuit.formats || [],
        circuitExercises: (circuit.circuitExercises || []).map(mapExercise),
        order: getOrder(), warmupTemplateId: templateId,
        warmupTemplateTitle: warmupLabel,
        warmupInstructions: templateInstructions,
      });
    });

    updateProgram(updatedProgram);
  };

  const addWarmupFromTemplate = (templateId: string, templateTitle: string) => {
    const cached = warmupDetailCache.current.get(templateId);
    if (cached) {
      applyWarmupData(cached, templateId, templateTitle, cached.instructions || '');
      showToast({ title: 'Warmup added', description: `Added "${templateTitle}"`, status: 'success' });
      return;
    }

    getWarmupDetail({
      callback: (data: any, error: string | null) => {
        if (error) {
          showToast({ title: 'Failed to fetch warmup details', description: error, status: 'error' });
          return;
        }
        if (!data) return;
        warmupDetailCache.current.set(templateId, data);
        applyWarmupData(data, templateId, templateTitle, data.instructions || '');
        showToast({ title: 'Warmup added', description: `Added "${templateTitle}"`, status: 'success' });
      },
      command: ApiCommand.GET,
      url: `${urlConstants.warmups.getWarmups}?warmupId=${templateId}`,
    });
  };

  useEffect(() => {
    if (isWeekCollapsed) setIsCollapsed(true);
  }, [isWeekCollapsed]);

  const addExercise = (
    weekIndex: number,
    dayIndex: number,
    newTypeId: number,
    newFormats: string[],
  ) => {
    const updatedProgram = _.cloneDeep(program);
    const currentCount = day.exercises.length + (day.circuits || []).length + (day.warmups || []).length; 
    const newExercise: DayExercise = {
      typeId: newTypeId,
      exerciseId: "",
      guide: "",
      sets: 0,
      reps: 0,
      weight: 0,
      rest: 0,
      per_side: false,
      custom_text: '',
      formats: newFormats,
      status: "",
      extra: [],
      order: currentCount + 1,
    };
    updatedProgram.weeks[weekIndex].days[dayIndex].exercises.push(newExercise);
    updateProgram(updatedProgram);
    setAddedExNum(newTypeId);
  };

  const updateDay = (
    weekIndex: number,
    dayIndex: number,
    updatedDay: Day,
    isTypeIdUpdate?: boolean,
    typeId?: number,
  ) => {
    const updatedProgram = _.cloneDeep(program);
    updatedProgram.weeks[weekIndex].days[dayIndex] = updatedDay;
    updateProgram(updatedProgram);
  };

  const duplicateDay = (weekIndex: number, dayIndex: number) => {
    const updatedProgram = _.cloneDeep(program);
    const originDay = updatedProgram.weeks[weekIndex].days[dayIndex];
    const newDay = _.cloneDeep(originDay);

    delete newDay._id;
    newDay.typeId = getNextTypeId();

    newDay.exercises.forEach((exercise) => {
      delete exercise._id;
    });
    (newDay.circuits || []).forEach((circuit) => {
      delete circuit._id;
      circuit.circuitExercises.forEach((exercise) => {
        delete exercise._id;
      });
    });
    (newDay.warmups || []).forEach((warmup) => {
      delete warmup._id;
      warmup.circuitExercises.forEach((exercise) => {
        delete exercise._id;
      });
    });

    updatedProgram.weeks[weekIndex].days.splice(dayIndex + 1, 0, newDay);
    updateProgram(updatedProgram);
  };

  const deleteDay = (weekIndex: number, dayIndex: number) => {
    const updatedProgram = _.cloneDeep(program);
    const weekDays = updatedProgram.weeks[weekIndex].days;
    const countSameType = weekDays.filter(
      (d) => d.typeId === day.typeId,
    ).length;

    weekDays.splice(dayIndex, 1);

    // Keep deletion and typeId normalization in the same state update.
    if (countSameType === 1) {
      weekDays.forEach((d) => {
        if (d.typeId > day.typeId) d.typeId--;
      });
    }

    updateProgram(updatedProgram);
  };

  const reassignExerciseTypeIds = (deletedExerciseTypeId: number) => {
    const updatedProgram = _.cloneDeep(program);
    const exercises = updatedProgram.weeks[weekIndex].days[dayIndex].exercises;
    exercises.forEach((exercise, index) => {
      if (
        exercise.typeId !== undefined &&
        exercise.typeId > deletedExerciseTypeId
      ) {
        exercise.typeId--;
      }
    });
    updateProgram(updatedProgram);
  };

  const updateDayTitle = (title: string) => {
    const updatedDay = { ...day, title };
    updateDay(weekIndex, dayIndex, updatedDay);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getTypeIdList = () => {
    return program.weeks[weekIndex].days.flatMap((day) => day.typeId);
  };

  const getNextTypeId = () => {
    const typeIds = getTypeIdList();
    const maxTypeId = Math.max(0, ...typeIds);
    return maxTypeId + 1;
  };

  const handleDayTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value;
    const shouldBeRestDay = selectedType === "rest";

    if (shouldBeRestDay !== isRestDay) {
      toggleDayMode();
    }
  };

  const toggleDayMode = () => {
    const nextIsRestDay = !isRestDay;

    const updatedProgram = _.cloneDeep(program);
    const currentDay = updatedProgram.weeks[weekIndex].days[dayIndex];

    currentDay.formats = nextIsRestDay
      ? Array.from(new Set([...(currentDay.formats || []), REST_DAY_FLAG]))
      : (currentDay.formats || []).filter((format) => format !== REST_DAY_FLAG);

    if (nextIsRestDay) {
      currentDay.restDayId = currentDay.restDayId || null;
      currentDay.exercises = [];
      currentDay.circuits = [];
      currentDay.warmups = [];
    } else {
      currentDay.restDayId = null;
    }

    updateProgram(updatedProgram);
  };

  const getNextExerciseTypeId = () => {
    const typeIds = day.exercises
      .map((exercise) => exercise.typeId)
      .filter((typeId): typeId is number => typeof typeId === "number");

    return Math.max(0, ...typeIds) + 1;
  };

  const getNextCircuitTypeId = () => {
    const typeIds = (day.circuits || [])
      .map((circuit) => circuit.typeId)
      .filter((typeId): typeId is number => typeof typeId === "number");

    return Math.max(0, ...typeIds) + 1;
  };

  const addCircuit = (weekIndex: number, dayIndex: number) => {
    const currentCount = day.exercises.length + (day.circuits || []).length + (day.warmups || []).length;  
    const updatedProgram = _.cloneDeep(program);
    const nextTypeId = getNextCircuitTypeId();
    const newCircuit: Circuit = {
      rounds: 1,
      title: `Circuit ${nextTypeId}`,
      typeId: nextTypeId,
      formats: [],
      circuitExercises: [],
      order: currentCount + 1,
    };

    if (!updatedProgram.weeks[weekIndex].days[dayIndex].circuits) {
      updatedProgram.weeks[weekIndex].days[dayIndex].circuits = [];
    }

    updatedProgram.weeks[weekIndex].days[dayIndex].circuits.push(newCircuit);
    updateProgram(updatedProgram);
  };

  const getNextWarmupTypeId = () => {
    const typeIds = (day.warmups || [])
      .map((warmup) => warmup.typeId)
      .filter((typeId): typeId is number => typeof typeId === "number");

    return Math.max(0, ...typeIds) + 1;
  };

  const addWarmup = (weekIndex: number, dayIndex: number) => {
    const currentCount = day.exercises.length + (day.circuits || []).length + (day.warmups || []).length;  
    const updatedProgram = _.cloneDeep(program);
    const nextTypeId = getNextWarmupTypeId();
    const newWarmup: Circuit = {
      rounds: 1,
      title: `Warmup ${nextTypeId}`,
      typeId: nextTypeId,
      formats: [],
      circuitExercises: [],
      order: currentCount + 1,
    };

    if (!updatedProgram.weeks[weekIndex].days[dayIndex].warmups) {
      updatedProgram.weeks[weekIndex].days[dayIndex].warmups = [];
    }

    updatedProgram.weeks[weekIndex].days[dayIndex].warmups!.push(newWarmup);
    updateProgram(updatedProgram);
  };

  const removeWarmup = (templateIdOrTitle: string) => {
    const updatedProgram = _.cloneDeep(program);
    updatedProgram.weeks[weekIndex].days[dayIndex].warmups =
      (updatedProgram.weeks[weekIndex].days[dayIndex].warmups || []).filter(
        (w) => w.warmupTemplateId
          ? w.warmupTemplateId !== templateIdOrTitle
          : w.title !== templateIdOrTitle,
      );
    updateProgram(updatedProgram);
  };

  const moveItem = (sortedIndex: number, direction: "up" | "down") => {
    const items = [
      ...day.exercises.map((exercise, exerciseIndex) => ({
        type: "exercise" as const,
        originalIndex: exerciseIndex,
        order: exercise.order ?? exerciseIndex,
      })),
      ...(day.circuits || []).map((circuit, circuitIndex) => ({
        type: "circuit" as const,
        originalIndex: circuitIndex,
        order: circuit.order ?? (day.exercises.length + circuitIndex),
      })),
      ...(day.warmups || []).map((warmup, warmupIndex) => ({
        type: "warmup" as const,
        originalIndex: warmupIndex,
        order: warmup.order ?? (day.exercises.length + (day.circuits || []).length + warmupIndex),
      })),
    ].sort((a, b) => a.order - b.order);

    const targetIndex = direction === "up" ? sortedIndex - 1 : sortedIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const updatedProgram = _.cloneDeep(program);
    const dayInProgram = updatedProgram.weeks[weekIndex].days[dayIndex];

    items.forEach((item, idx) => {
      let finalOrder = idx;
      if (idx === sortedIndex) {
        finalOrder = targetIndex;
      } else if (idx === targetIndex) {
        finalOrder = sortedIndex;
      }

      if (item.type === "exercise") {
        dayInProgram.exercises[item.originalIndex].order = finalOrder;
      } else if (item.type === "circuit") {
        dayInProgram.circuits[item.originalIndex].order = finalOrder;
      } else if (item.type === "warmup") {
        dayInProgram.warmups![item.originalIndex].order = finalOrder;
      }
    });

    updateProgram(updatedProgram);
  };

  const moveItemToTarget = (fromIndex: number, toIndex: number) => {
    const items = [
      ...day.exercises.map((exercise, exerciseIndex) => ({
        type: "exercise" as const,
        originalIndex: exerciseIndex,
        order: exercise.order ?? exerciseIndex,
      })),
      ...(day.circuits || []).map((circuit, circuitIndex) => ({
        type: "circuit" as const,
        originalIndex: circuitIndex,
        order: circuit.order ?? (day.exercises.length + circuitIndex),
      })),
      ...(day.warmups || []).map((warmup, warmupIndex) => ({
        type: "warmup" as const,
        originalIndex: warmupIndex,
        order: warmup.order ?? (day.exercises.length + (day.circuits || []).length + warmupIndex),
      })),
    ].sort((a, b) => a.order - b.order);

    const updatedProgram = _.cloneDeep(program);
    const dayInProgram = updatedProgram.weeks[weekIndex].days[dayIndex];

    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);

    items.forEach((item, idx) => {
      if (item.type === "exercise") {
        dayInProgram.exercises[item.originalIndex].order = idx;
      } else if (item.type === "circuit") {
        dayInProgram.circuits[item.originalIndex].order = idx;
      } else if (item.type === "warmup") {
        dayInProgram.warmups![item.originalIndex].order = idx;
      }
    });

    updateProgram(updatedProgram);
  };

  return (
    <Box
      p={4}
      bg="primary.100"
      borderRadius="md"
      boxShadow="md"
      my={4}
      className={`day-${dayIndex}`}
    >
      <Flex justify="space-between" align="center">
        <Box flex="1" mr={5}>
          <CustomTitle
            type={"Workout"}
            index={dayIndex + 1}
            customTitle={day.title  || `Workout ${dayIndex + 1}`}
            updateFunction={updateDayTitle}
          />
        </Box>

        {/* add switch button "workout day and rest day" */}
        <FormControl w="auto" mr={4}>
          <Flex align="center" gap={3}>
            <FormLabel
              htmlFor={`day-${dayIndex}-type`}
              mb="0"
              whiteSpace="nowrap"
            >
              Workout Type
            </FormLabel>
            <Select
              id={`day-${dayIndex}-type`}
              value={isRestDay ? "rest" : "workout"}
              onChange={handleDayTypeChange}
              bg="white"
              w="180px"
            >
              <option value="workout">Workout Day</option>
              <option value="rest">Rest Day</option>
            </Select>
          </Flex>
        </FormControl>

        {totaldays < 7 && (
          <Button
            backgroundColor="primary.300"
            leftIcon={<CopyIcon mr={"-2"} />}
            _hover={{ bg: "primary.500" }}
            mr={2}
            onClick={() => duplicateDay(weekIndex, dayIndex)}
          />
        )}

        <Button
          backgroundColor="primary.300"
          leftIcon={<DeleteIcon mr={"-2"} />}
          _hover={{ bg: "primary.500" }}
          mr={5}
          onClick={() => deleteDay(weekIndex, dayIndex)}
        />
        <Button
          backgroundColor="primary.300"
          leftIcon={
            isCollapsed ? (
              <ChevronDownIcon mr={"-2"} />
            ) : (
              <ChevronUpIcon mr={"-2"} />
            )
          }
          onClick={toggleCollapse}
          _hover={{ bg: "primary.500" }}
        />
      </Flex>
      <Collapse in={!isCollapsed} animateOpacity>
        {!isRestDay ? (
          <>
            <DayDetail
              weekIndex={weekIndex}
              dayIndex={dayIndex}
              day={day}
              updateDay={updateDay}
              onThumbnailFile={onDayThumbnailFile}
              warmupTemplates={warmupTemplates}
              selectedWarmups={day.warmups || []}
              addWarmupFromTemplate={addWarmupFromTemplate}
              removeWarmup={removeWarmup}
            />

            {[...day.exercises.map((exercise, exerciseIndex)=> ({
            type: 'exercise' as const,
            order: exercise.order ?? exerciseIndex,
            data: exercise,
            index: exerciseIndex,
          })),
          ...(day.circuits || []).map((circuit,circuitIndex) => ({
            type: 'circuit' as const,
            order: circuit.order ?? (day.exercises.length + circuitIndex),
            data: circuit,
            index: circuitIndex,
          })),
        ]
          .sort((a, b) => a.order - b.order)             
          .map((item, sortedIndex, arr) => {
            if (!item || !item.data) return null;
            const isFirst = sortedIndex === 0;
            const isLast = sortedIndex === arr.length - 1;
            let element;
            if (item.type === 'exercise') {
              element = (
                <ExercisePlan             
                  key={item.data._id ?? `exercise-${item.data.typeId ?? item.index}`}    
                  weekIndex={weekIndex}                    
                  dayIndex={dayIndex}                      
                  exerciseIndex={item.index}               
                  exercise={item.data}                   
                  addExercise={addExercise}               
                  reassignExerciseTypeIds={reassignExerciseTypeIds}
                  program={program}                        
                  updateProgram={updateProgram}            
                  days={days}                  
                  updateDays={updateDays}                  
                  addedExNum={addedExNum}
                />
              );
            } else if (item.type === 'circuit') {
              element = (
                <CircuitPlan                             
                  key={item.data._id ?? `circuit-${item.data.typeId ?? item.index}`}                   
                  weekIndex={weekIndex} 
                  dayIndex={dayIndex}                      
                  circuitIndex={item.index}
                  circuit={item.data}                    
                  program={program}                        
                  updateProgram={updateProgram}
                />
              );
            }

            return (
              <Box
                key={item.data._id ?? `${item.type}-${item.data.typeId ?? item.index}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", String(sortedIndex));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  const dragIndex = Number(e.dataTransfer.getData("text/plain"));
                  if (!isNaN(dragIndex) && dragIndex !== sortedIndex) {
                    moveItemToTarget(dragIndex, sortedIndex);
                  }
                }}
                border="1px solid transparent"
                _hover={{ borderColor: "gray.200", borderRadius: "md" }}
              >
                <Flex
                  justify="space-between"
                  align="center"
                  mt={2}
                  px={1}
                  cursor="grab"
                  _active={{ cursor: "grabbing" }}
                >
                  <Box fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                    {item.type} Order
                  </Box>
                  <Flex gap={2}>
                    <Button
                      size="xs"
                      colorScheme="primary"
                      variant="solid"
                      isDisabled={isFirst}
                      onClick={() => moveItem(sortedIndex, 'up')}
                      leftIcon={<ChevronUpIcon />}
                    >
                      Move Up
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="primary"
                      variant="solid"
                      isDisabled={isLast}
                      onClick={() => moveItem(sortedIndex, 'down')}
                      leftIcon={<ChevronDownIcon />}
                    >
                      Move Down
                    </Button>
                    <Box display="flex" alignItems="center" pl={2} cursor="grab" _active={{ cursor: "grabbing" }}>
                      <DragHandleIcon color="gray.400" />
                    </Box>
                  </Flex>
                </Flex>
                {element}
              </Box>
            );
          })}  

            {/* {day.exercises.map((exercise, exerciseIndex) => (
              <ExercisePlan
                key={exercise._id ?? `exercise-${exercise.typeId ?? exerciseIndex}`}
                weekIndex={weekIndex}
                dayIndex={dayIndex}
                exerciseIndex={exerciseIndex}
                exercise={exercise}
                addExercise={addExercise}
                reassignExerciseTypeIds={reassignExerciseTypeIds}
                program={program}
                updateProgram={updateProgram}
                days={days}
                updateDays={updateDays}
                addedExNum={addedExNum}
              />
            ))}

            {(day.circuits || []).map((circuit, circuitIndex) => (
              <CircuitPlan
                key={circuit._id ?? `circuit-${circuit.typeId}`}
                weekIndex={weekIndex}
                dayIndex={dayIndex}
                circuitIndex={circuitIndex}
                circuit={circuit}
                program={program}
                updateProgram={updateProgram}
              />
            ))} */}
            <Flex mt={4} gap={3}>
              <Button
                colorScheme="primary"
                leftIcon={<AddIcon />}
                onClick={() =>
                  addExercise(weekIndex, dayIndex, getNextExerciseTypeId(), [])
                }
              >
                Add Exercise
              </Button>

              <Button
                colorScheme="primary"
                variant="outline"
                leftIcon={<AddIcon />}
                onClick={() => addCircuit(weekIndex, dayIndex)}
                ml={3}
              >
                Add Circuit
              </Button>
            </Flex>
          </>
        ) : (
          <RestDayPlan
            weekIndex={weekIndex}
            dayIndex={dayIndex}
            day={day}
            program={program}
            updateProgram={updateProgram}
          />
        )}
      </Collapse>
    </Box>
  );
};
